/**
 * Web automation runs — timeline of desktop runner executions with
 * final output review and human Validate / Reject decisions.
 */

import { useEffect, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Eye as ViewIcon, CheckCircle as ValidateIcon, XCircle as RejectIcon, Copy as CopyIcon, ChevronDown as ExpandMoreIcon, RefreshCw as RefreshIcon } from 'lucide-react';
import { Button, Chip, CoordioDropdown, ShellHeader, Textarea, Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '@coordio/ui';
import { EmptyState } from '@coordio/ui/layout';

import { webAutomationService, type WebAutomationRunDto } from '../services/webAutomationService';
import { useNotificationStore } from '../store/notificationStore';
import { useContextStore } from '../store/contextStore';
import DataTable, { type DataTableColumn } from '../components/data-display/DataTable';
import { PageListLayout } from '../components/layouts/PageListLayout';

const STATUS_COLORS: Record<string, 'green' | 'red' | 'blue' | 'amber' | 'default'> = {
  Succeeded: 'green',
  Failed: 'red',
  Running: 'blue',
  Pending: 'amber',
  Cancelled: 'default',
};

const VALIDATION_COLORS: Record<string, 'green' | 'red' | 'default'> = {
  Validated: 'green',
  Rejected: 'red',
  NotReviewed: 'default',
};

/** Statuses for which the run detail dialog shows the phase chip / heartbeat age. */
const isActiveStatus = (status: string) => status === 'Pending' || status === 'Running';

const formatHeartbeatAge = (iso?: string) => {
  if (!iso) return null;
  const seconds = Math.max(0, Math.round((Date.now() - new Date(iso).getTime()) / 1000));
  return seconds < 120 ? `${seconds}s ago` : `${Math.round(seconds / 60)}m ago`;
};

/**
 * Scrollable monospace panel with a header and Copy button, mirroring the
 * desktop runner layout (Activity Log / Generated Script / Final Output).
 */
const RunDetailPanel = ({
  title,
  content,
  emptyText,
  height,
  onCopied,
}: {
  title: string;
  content?: string | null;
  emptyText: string;
  height: number;
  onCopied: (title: string) => void;
}) => {
  const handleCopy = async () => {
    if (!content) return;
    await navigator.clipboard.writeText(content);
    onCopied(title);
  };

  return (
    <div className="flex flex-col" style={{ height, minHeight: 0 }}>
      <div className="flex items-center justify-between mb-1">
        <h4 className="text-sm font-semibold">{title}</h4>
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <span>
                <Button type="button" variant="ghost" size="icon" onClick={handleCopy} disabled={!content}>
                  <CopyIcon size={16} />
                </Button>
              </span>
            </TooltipTrigger>
            <TooltipContent>{`Copy ${title.toLowerCase()}`}</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      </div>
      <div
        className="flex-1 overflow-auto rounded border border-[var(--color-border-default)] bg-[var(--color-bg-subtle)] p-4"
        style={{ minHeight: 0 }}
      >
        {content ? (
          <pre className="text-xs font-mono whitespace-pre-wrap m-0">{content}</pre>
        ) : (
          <span className="text-xs text-[var(--color-text-secondary)]">{emptyText}</span>
        )}
      </div>
    </div>
  );
};

export const WebAutomationRunsPage = () => {
  const [searchParams] = useSearchParams();
  const siteTaskIdFilter = searchParams.get('siteTaskId') || undefined;
  const { addNotification } = useNotificationStore();
  const currentTenant = useContextStore((s) => s.currentTenant);
  const currentProject = useContextStore((s) => s.currentProject);
  const hasContext = Boolean(currentTenant && currentProject);

  const [runs, setRuns] = useState<WebAutomationRunDto[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [statusFilter, setStatusFilter] = useState('');
  const [selectedRun, setSelectedRun] = useState<WebAutomationRunDto | null>(null);
  const [validationNotes, setValidationNotes] = useState('');
  const [accordionOpen, setAccordionOpen] = useState(false);

  const loadRuns = async () => {
    setLoading(true);
    setError(null);
    try {
      setRuns(await webAutomationService.getRuns(siteTaskIdFilter, statusFilter || undefined));
    } catch (err: any) {
      setError(err.message || 'Failed to load runs');
      addNotification('Failed to load web automation runs', 'error');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (!hasContext) return;
    loadRuns();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [siteTaskIdFilter, statusFilter, currentTenant?.id, currentProject?.id]);

  const refreshRun = async (runId: string) => {
    try {
      const fresh = await webAutomationService.getRun(runId);
      setSelectedRun((current) => (current && current.id === runId ? fresh : current));
      setRuns((current) => current.map((r) => (r.id === runId ? fresh : r)));
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to refresh run', 'error');
    }
  };

  const openRun = (run: WebAutomationRunDto) => {
    setSelectedRun(run);
    setValidationNotes(run.validationNotes || '');
    setAccordionOpen(false);
    refreshRun(run.id);
  };

  const handleValidate = async (run: WebAutomationRunDto, decision: 'Validated' | 'Rejected') => {
    try {
      await webAutomationService.validateRun(run.id, {
        validationStatus: decision,
        notes: validationNotes.trim() || undefined,
      });
      addNotification(`Run ${decision.toLowerCase()}`, 'success');
      setSelectedRun(null);
      setValidationNotes('');
      loadRuns();
    } catch (err: any) {
      addNotification(err.response?.data?.message || 'Failed to record validation', 'error');
    }
  };

  const handleCopied = (title: string) => addNotification(`${title} copied to clipboard`, 'success');

  const formatDuration = (run: WebAutomationRunDto) => {
    if (!run.startedAt || !run.completedAt) return '—';
    const ms = new Date(run.completedAt).getTime() - new Date(run.startedAt).getTime();
    return ms < 60000 ? `${Math.round(ms / 1000)}s` : `${Math.round(ms / 60000)}m ${Math.round((ms % 60000) / 1000)}s`;
  };

  const columns: DataTableColumn<WebAutomationRunDto>[] = [
    {
      id: 'siteTask',
      label: 'Site / Task',
      minWidth: 220,
      render: (run) => (
        <div>
          <p className="text-sm font-medium">{run.siteName}</p>
          <span className="text-xs text-[var(--color-text-secondary)]">{run.taskName}</span>
        </div>
      ),
    },
    {
      id: 'status',
      label: 'Status',
      minWidth: 110,
      render: (run) => (
        <div>
          <Chip label={run.status} color={STATUS_COLORS[run.status] || 'default'} />
          {run.status === 'Running' && run.phase && (
            <span className="block text-xs text-[var(--color-text-secondary)] mt-0.5">{run.phase}</span>
          )}
        </div>
      ),
    },
    {
      id: 'script',
      label: 'Script',
      minWidth: 90,
      render: (run) => (
        <span className="text-sm">{run.scriptVersion ? `v${run.scriptVersion}` : '—'}</span>
      ),
    },
    {
      id: 'started',
      label: 'Started',
      minWidth: 160,
      render: (run) => (
        <span className="text-sm">{run.startedAt ? new Date(run.startedAt).toLocaleString() : '—'}</span>
      ),
    },
    {
      id: 'duration',
      label: 'Duration',
      minWidth: 90,
      render: (run) => <span className="text-sm">{formatDuration(run)}</span>,
    },
    {
      id: 'validation',
      label: 'Review',
      minWidth: 120,
      render: (run) => (
        <Chip
          label={run.validationStatus === 'NotReviewed' ? 'Not reviewed' : run.validationStatus}
          color={VALIDATION_COLORS[run.validationStatus] || 'default'}
          variant={run.validationStatus === 'NotReviewed' ? 'outlined' : undefined}
        />
      ),
    },
    {
      id: 'actions',
      label: 'Actions',
      minWidth: 100,
      align: 'right',
      render: (run) => (
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger asChild>
              <Button type="button" variant="ghost" size="icon" onClick={() => openRun(run)}>
                <ViewIcon size={16} />
              </Button>
            </TooltipTrigger>
            <TooltipContent>Review output & validate</TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ),
    },
  ];

  if (!hasContext) {
    return (
      <div className="web-automation-runs" data-testid="web-automation-run-list-page">
        <ShellHeader
          title="Web Automation Runs"
          description="Timeline of desktop runner executions with final output review and human validation decisions."
          data-testid="web-automation-run-list-header"
        />
        <EmptyState
          title="No workspace and project selected"
          description="Runs are project-scoped. Choose a workspace and a project from the switchers above to view web automation runs."
          data-testid="web-automation-run-empty-state"
        />
      </div>
    );
  }

  return (
    <div data-testid="web-automation-run-list-page">
      <PageListLayout
        title="Web Automation Runs"
        error={error || undefined}
        onErrorDismiss={() => setError(null)}
        loading={loading}
        isEmpty={runs.length === 0}
        emptyState={{
          title: 'No runs yet',
          description:
            'Queue a run from a site detail page — the desktop runner picks up Pending runs and executes them.',
        }}
        filters={
          <div className="flex gap-4 flex-wrap items-center">
            <div className="min-w-[180px]">
              <CoordioDropdown
                options={[
                  { value: '', label: 'All' },
                  ...Object.keys(STATUS_COLORS).map((s) => ({ value: s, label: s })),
                ]}
                value={statusFilter}
                onChange={(v) => { if (v !== null) setStatusFilter(v); }}
                placeholder="Status"
              />
            </div>
            {siteTaskIdFilter && (
              <Chip label={`SiteTask: ${siteTaskIdFilter.substring(0, 8)}…`} />
            )}
            <TooltipProvider>
              <Tooltip>
                <TooltipTrigger asChild>
                  <span>
                    <Button type="button" variant="ghost" size="icon" onClick={loadRuns} disabled={loading}>
                      <RefreshIcon size={16} />
                    </Button>
                  </span>
                </TooltipTrigger>
                <TooltipContent>Refresh runs</TooltipContent>
              </Tooltip>
            </TooltipProvider>
          </div>
        }
      >
        <div data-testid="web-automation-run-list-grid">
          <DataTable columns={columns} rows={runs} />
        </div>
      </PageListLayout>

      {/* Run detail + validation dialog */}
      {selectedRun && (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
          <div className="fixed inset-0 bg-black/50" onClick={() => setSelectedRun(null)} />
          <div className="relative z-10 w-full max-w-5xl rounded-xl border border-[var(--color-border-default)] bg-[var(--color-bg-surface)] shadow-xl max-h-[90vh] flex flex-col">
            {/* Title */}
            <div className="border-b border-[var(--color-border-default)] px-6 py-4">
              <div className="flex items-center gap-2 flex-wrap">
                <h2 className="text-lg font-semibold">
                  {selectedRun.siteName} — {selectedRun.taskName}
                </h2>
                <Chip label={selectedRun.status} color={STATUS_COLORS[selectedRun.status] || 'default'} />
                {isActiveStatus(selectedRun.status) && selectedRun.phase && (
                  <Chip label={selectedRun.phase} variant="outlined" />
                )}
                {isActiveStatus(selectedRun.status) && selectedRun.lastHeartbeatAt && (
                  <span className="text-xs text-[var(--color-text-secondary)]">
                    heartbeat {formatHeartbeatAge(selectedRun.lastHeartbeatAt)}
                  </span>
                )}
                <div className="flex-grow" />
                <TooltipProvider>
                  <Tooltip>
                    <TooltipTrigger asChild>
                      <Button type="button" variant="ghost" size="icon" onClick={() => refreshRun(selectedRun.id)}>
                        <RefreshIcon size={16} />
                      </Button>
                    </TooltipTrigger>
                    <TooltipContent>Refresh run status, activity log & output</TooltipContent>
                  </Tooltip>
                </TooltipProvider>
              </div>
            </div>
            {/* Content */}
            <div className="flex-1 overflow-y-auto px-6 py-4 border-b border-[var(--color-border-default)]">
              {selectedRun.errorMessage && (
                <div className="mb-6">
                  <h4 className="text-sm font-semibold text-red-600 mb-1">Error</h4>
                  <p className="text-sm font-mono whitespace-pre-wrap">{selectedRun.errorMessage}</p>
                </div>
              )}

              {/* Desktop-parity 3-panel layout */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
                <RunDetailPanel
                  title="Activity Log"
                  content={selectedRun.activityLog}
                  emptyText="No activity log was captured for this run (older runs predate log capture)."
                  height={424}
                  onCopied={handleCopied}
                />
                <div className="flex flex-col gap-4">
                  <RunDetailPanel
                    title="Generated Script (JSON)"
                    content={selectedRun.generatedScriptJson}
                    emptyText="No script snapshot — discovery may have failed before a script was generated."
                    height={204}
                    onCopied={handleCopied}
                  />
                  <RunDetailPanel
                    title="Final Output (Markdown)"
                    content={selectedRun.finalOutput}
                    emptyText="(no output)"
                    height={204}
                    onCopied={handleCopied}
                  />
                </div>
              </div>

              {/* Collapsible additional details (replaces MUI Accordion) */}
              {(selectedRun.agentSessionId || selectedRun.artifactsJson || selectedRun.stepsJson) && (
                <details className="mb-6 rounded-lg border border-[var(--color-border-default)]" open={accordionOpen}>
                  <summary
                    className="flex cursor-pointer items-center justify-between px-4 py-3 text-sm font-semibold select-none"
                    onClick={(e) => {
                      e.preventDefault();
                      setAccordionOpen(!accordionOpen);
                    }}
                  >
                    Additional details
                    <ExpandMoreIcon className={`transition-transform duration-200 ${accordionOpen ? 'rotate-180' : ''}`} size={16} />
                  </summary>
                  {accordionOpen && (
                    <div className="border-t border-[var(--color-border-default)] px-4 py-3">
                      {selectedRun.agentSessionId && (
                        <div className="mb-6">
                          <h4 className="text-sm font-semibold mb-1">Agent Session</h4>
                          <p className="text-sm font-mono">{selectedRun.agentSessionId}</p>
                          <span className="block text-xs text-[var(--color-text-secondary)] mt-1">
                            Join this session in SignalR (`JoinSession`) for live thinking/tool/subagent events while
                            the desktop AgentRunner slow path is active.
                          </span>
                        </div>
                      )}

                      {selectedRun.artifactsJson && (
                        <div className="mb-6">
                          <h4 className="text-sm font-semibold mb-1">Artifacts</h4>
                          <div className="max-h-[200px] overflow-auto rounded border border-[var(--color-border-default)] bg-[var(--color-bg-subtle)] p-4">
                            <pre className="text-xs font-mono whitespace-pre-wrap">{selectedRun.artifactsJson}</pre>
                          </div>
                        </div>
                      )}

                      {selectedRun.stepsJson && (
                        <div>
                          <h4 className="text-sm font-semibold mb-1">Executed Steps</h4>
                          <div className="max-h-[200px] overflow-auto rounded border border-[var(--color-border-default)] p-4">
                            <pre className="text-xs font-mono whitespace-pre-wrap">{selectedRun.stepsJson}</pre>
                          </div>
                        </div>
                      )}
                    </div>
                  )}
                </details>
              )}

              {/* Validation notes */}
              <div className="flex flex-col gap-1">
                <label className="text-sm font-medium" htmlFor="validation-notes">Review notes</label>
                <Textarea
                  id="validation-notes"
                  rows={2}
                  value={validationNotes}
                  onChange={(e) => setValidationNotes(e.target.value)}
                />
                <span className="text-xs text-[var(--color-text-secondary)]">Optional notes recorded with your Validate / Reject decision</span>
              </div>
            </div>
            {/* Actions */}
            <div className="flex justify-end gap-2 px-6 py-4">
              <Button type="button" variant="ghost" size="sm" onClick={() => setSelectedRun(null)}>Close</Button>
              <Button
                type="button"
                variant="destructive"
                size="sm"
                className="gap-2"
                onClick={() => handleValidate(selectedRun, 'Rejected')}
              >
                <RejectIcon size={16} />
                Reject
              </Button>
              <Button
                type="button"
                variant="default"
                size="sm"
                className="gap-2"
                onClick={() => handleValidate(selectedRun, 'Validated')}
              >
                <ValidateIcon size={16} />
                Validate
              </Button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
